import { buildCoachTranscriptReviewRoute } from "@/lib/coachTranscriptReviewRoute";

export type LeaderDetailInputState<T> =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; value: T };

export interface LeaderDetailDecisionScope {
  coachId: string;
  leaderId: string;
  relationshipRevision: string;
  accessGeneration: string;
  mode: "live" | "demo";
}

export type LeaderDetailAccessState =
  | { status: "checking" }
  | { status: "error" }
  | { status: "unavailable" }
  | { status: "authorized" };

export interface LeaderDetailReviewTranscriptInput {
  id: string;
  title: string;
  createdAt: string;
  requiresProcessing: boolean;
}

export interface LeaderDetailUpcomingMeetingInput {
  coachCallId: string;
  title: string;
  startsAt: string;
  status: "scheduled" | "cancelled" | "completed";
}

export type LeaderDetailMissingContextInput =
  | {
      kind: "completed_meeting_without_evidence";
      coachCallId: string;
      meetingTitle: string;
      occurredAt: string;
    }
  | { kind: "no_upcoming_meeting" }
  | null;

export interface LeaderDetailDecisionInput {
  scope: LeaderDetailDecisionScope;
  access: LeaderDetailAccessState;
  billing: LeaderDetailInputState<{
    pendingResolution: boolean;
    processingLimitReached: boolean;
  }>;
  workspace: LeaderDetailInputState<{ ready: true }>;
  review: LeaderDetailInputState<{
    transcripts: LeaderDetailReviewTranscriptInput[];
    pendingEvidenceCount: number;
    newestEvidenceAt: string | null;
  }>;
  upcoming: LeaderDetailInputState<{
    meetings: LeaderDetailUpcomingMeetingInput[];
  }>;
  context: LeaderDetailInputState<{
    missing: LeaderDetailMissingContextInput;
  }>;
  sourceReadiness: LeaderDetailInputState<{
    unavailableLabels: string[];
    affectsPrimaryAction: boolean;
  }>;
  auxiliaryReadiness: LeaderDetailInputState<{
    unavailableLabels: string[];
  }>;
  now: string;
}

interface LeaderDetailActionBase {
  id: string;
  label: string;
  title: string;
  description: string;
  route: string;
  sourceLabel: string;
  sourceDate: string | null;
  isWrite: boolean;
  requiresProcessing: boolean;
}

export interface LeaderDetailReviewTranscriptAction extends LeaderDetailActionBase {
  kind: "review_transcript";
  transcriptId: string;
}

export interface LeaderDetailReviewEvidenceAction extends LeaderDetailActionBase {
  kind: "review_evidence";
  pendingEvidenceCount: number;
}

export interface LeaderDetailPrepareMeetingAction extends LeaderDetailActionBase {
  kind: "prepare_meeting";
  coachCallId: string;
}

export interface LeaderDetailUploadTranscriptAction extends LeaderDetailActionBase {
  kind: "upload_transcript";
  coachCallId: string;
}

export interface LeaderDetailScheduleMeetingAction extends LeaderDetailActionBase {
  kind: "schedule_meeting";
}

export interface LeaderDetailDemoContextAction extends LeaderDetailActionBase {
  kind: "demo_context";
  contextKind: "completed_meeting_without_evidence" | "no_upcoming_meeting";
}

export type LeaderDetailPrimaryAction =
  | LeaderDetailReviewTranscriptAction
  | LeaderDetailReviewEvidenceAction
  | LeaderDetailPrepareMeetingAction
  | LeaderDetailUploadTranscriptAction
  | LeaderDetailScheduleMeetingAction
  | LeaderDetailDemoContextAction;

export type LeaderDetailRecoveryAction =
  | { kind: "back_to_leaders"; label: "Back to Leaders"; route: "/coach/leaders" }
  | { kind: "retry_access" | "retry_billing" | "retry_workspace" | "retry_review" | "retry_upcoming" | "retry_context"; label: "Try again"; route: null };

export interface LeaderDetailOperationalNotice {
  kind: "billing_resolution" | "processing_limit" | "source_readiness" | "auxiliary_unavailable";
  tone: "warning" | "critical";
  title: string;
  description: string;
  action: null | {
    kind: "reveal_billing_resolution" | "retry_source_context" | "retry_auxiliary_details";
    label: string;
  };
}

export type LeaderDetailDecisionPresentation =
  | {
      status: "loading";
      scope: LeaderDetailDecisionScope;
      loadingFamily: "access" | "billing" | "workspace" | "review" | "upcoming" | "context";
      primaryAction: null;
      notice: null;
    }
  | {
      status: "unavailable";
      scope: LeaderDetailDecisionScope;
      reason: "access_error" | "access_unavailable" | "billing_error" | "workspace_error" | "review_error" | "upcoming_error" | "context_error" | "source_error" | "invalid_source";
      recoveryAction: LeaderDetailRecoveryAction;
      primaryAction: null;
      notice: null;
    }
  | {
      status: "ready";
      scope: LeaderDetailDecisionScope;
      primaryAction: LeaderDetailPrimaryAction | null;
      notice: LeaderDetailOperationalNotice | null;
    };

function isStableId(value: string): boolean {
  return /^[A-Za-z0-9_-]+$/.test(value.trim());
}

function isCanonicalDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/.test(value.trim())
    && Number.isFinite(Date.parse(value));
}

function retryAction(kind: Extract<LeaderDetailRecoveryAction, { route: null }>["kind"]): LeaderDetailRecoveryAction {
  return { kind, label: "Try again", route: null };
}

function loading(
  scope: LeaderDetailDecisionScope,
  loadingFamily: Extract<LeaderDetailDecisionPresentation, { status: "loading" }>["loadingFamily"],
): LeaderDetailDecisionPresentation {
  return { status: "loading", scope, loadingFamily, primaryAction: null, notice: null };
}

function unavailable(
  scope: LeaderDetailDecisionScope,
  reason: Extract<LeaderDetailDecisionPresentation, { status: "unavailable" }>["reason"],
  recoveryAction: LeaderDetailRecoveryAction,
): LeaderDetailDecisionPresentation {
  return { status: "unavailable", scope, reason, recoveryAction, primaryAction: null, notice: null };
}

function leaderRoute(leaderId: string, params: Record<string, string>): string {
  const search = new URLSearchParams(params);
  return `/coach/leaders/${encodeURIComponent(leaderId)}?${search.toString()}`;
}

function resolvePrimaryAction(
  input: LeaderDetailDecisionInput,
): LeaderDetailPrimaryAction | LeaderDetailDecisionPresentation | null {
  const { scope } = input;
  if (input.review.status === "loading") return loading(scope, "review");
  if (input.review.status === "error") return unavailable(scope, "review_error", retryAction("retry_review"));
  if (
    !Number.isInteger(input.review.value.pendingEvidenceCount)
    || input.review.value.pendingEvidenceCount < 0
  ) return unavailable(scope, "invalid_source", retryAction("retry_review"));

  if (input.review.value.transcripts.some((candidate) => (
    !isStableId(candidate.id) || !isCanonicalDate(candidate.createdAt)
  ))) return unavailable(scope, "invalid_source", retryAction("retry_review"));

  const transcripts = [...input.review.value.transcripts].sort((a, b) => (
    Date.parse(b.createdAt) - Date.parse(a.createdAt) || a.id.localeCompare(b.id)
  ));
  const transcript = transcripts[0];
  if (transcript) {
    const route = scope.mode === "demo"
      ? leaderRoute(scope.leaderId, { workspace: "review" })
      : buildCoachTranscriptReviewRoute(transcript.id, { leaderId: scope.leaderId, from: "leaders" });
    return {
      id: `review-transcript:${transcript.id}`,
      kind: "review_transcript",
      transcriptId: transcript.id,
      label: "Review transcript",
      title: transcript.title.trim() || "Transcript ready for review",
      description: scope.mode === "demo"
        ? "Open the on-page demo review. Synthetic demo transcripts never navigate to a live record."
        : "Review the latest transcript before using it as durable coaching context.",
      route,
      sourceLabel: scope.mode === "demo" ? "Demo transcript" : "Transcript review",
      sourceDate: transcript.createdAt,
      isWrite: false,
      requiresProcessing: transcript.requiresProcessing,
    };
  }
  if (input.review.value.pendingEvidenceCount > 0) {
    if (
      !input.review.value.newestEvidenceAt
      || !isCanonicalDate(input.review.value.newestEvidenceAt)
    ) return unavailable(scope, "invalid_source", retryAction("retry_review"));
    return {
      id: "review-evidence",
      kind: "review_evidence",
      pendingEvidenceCount: input.review.value.pendingEvidenceCount,
      label: "Review evidence",
      title: `${input.review.value.pendingEvidenceCount} evidence item${input.review.value.pendingEvidenceCount === 1 ? "" : "s"} ready`,
      description: "Open the leader's evidence queue and review each exact source before it is treated as complete.",
      route: leaderRoute(scope.leaderId, { workspace: "review" }),
      sourceLabel: "Evidence review queue",
      sourceDate: input.review.value.newestEvidenceAt,
      isWrite: false,
      requiresProcessing: false,
    };
  }

  if (input.upcoming.status === "loading") return loading(scope, "upcoming");
  if (input.upcoming.status === "error") return unavailable(scope, "upcoming_error", retryAction("retry_upcoming"));
  if (!isCanonicalDate(input.now)) return unavailable(scope, "invalid_source", retryAction("retry_upcoming"));
  if (input.upcoming.value.meetings.some((candidate) => (
    candidate.status === "scheduled"
    && (!isStableId(candidate.coachCallId) || !isCanonicalDate(candidate.startsAt))
  ))) return unavailable(scope, "invalid_source", retryAction("retry_upcoming"));
  const now = Date.parse(input.now);
  const upcomingMeetings = input.upcoming.value.meetings
    .filter((meeting) => meeting.status === "scheduled" && Date.parse(meeting.startsAt) >= now)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.coachCallId.localeCompare(b.coachCallId));
  const meeting = upcomingMeetings[0];
  if (meeting) {
    const route = leaderRoute(scope.leaderId, {
      workspace: "brief",
      meetingId: meeting.coachCallId,
    });
    return {
      id: `prepare-meeting:${meeting.coachCallId}`,
      kind: "prepare_meeting",
      coachCallId: meeting.coachCallId,
      label: "Prepare for meeting",
      title: meeting.title.trim() || "Upcoming coaching conversation",
      description: "Open the exact scheduled meeting and prepare with this leader's current context.",
      route,
      sourceLabel: scope.mode === "demo" ? "Demo scheduled meeting" : "Scheduled coach call",
      sourceDate: meeting.startsAt,
      isWrite: false,
      requiresProcessing: false,
    };
  }

  if (input.context.status === "loading") return loading(scope, "context");
  if (input.context.status === "error") return unavailable(scope, "context_error", retryAction("retry_context"));
  const missing = input.context.value.missing;
  if (missing?.kind === "completed_meeting_without_evidence") {
    if (!isStableId(missing.coachCallId) || !isCanonicalDate(missing.occurredAt)) {
      return unavailable(scope, "invalid_source", retryAction("retry_context"));
    }
    if (scope.mode === "demo") {
      return {
        id: "demo-missing-evidence",
        kind: "demo_context",
        contextKind: missing.kind,
        label: "Review demo context",
        title: missing.meetingTitle.trim() || "Demo meeting context",
        description: "Review the on-page demo state. Demo data never opens a live upload workflow.",
        route: leaderRoute(scope.leaderId, { workspace: "review" }),
        sourceLabel: "Demo completed meeting",
        sourceDate: missing.occurredAt,
        isWrite: false,
        requiresProcessing: false,
      };
    }
    const search = new URLSearchParams({
      upload: "1",
      leaderId: scope.leaderId,
      meetingId: missing.coachCallId,
    });
    return {
      id: `upload-transcript:${missing.coachCallId}`,
      kind: "upload_transcript",
      coachCallId: missing.coachCallId,
      label: "Add meeting context",
      title: missing.meetingTitle.trim() || "Meeting context is missing",
      description: "Upload the transcript for this known completed meeting so its evidence can enter review.",
      route: `/coach/reviews?${search.toString()}`,
      sourceLabel: "Completed meeting without evidence",
      sourceDate: missing.occurredAt,
      isWrite: true,
      requiresProcessing: true,
    };
  }
  if (missing?.kind === "no_upcoming_meeting") {
    if (scope.mode === "demo") {
      return {
        id: "demo-no-upcoming-meeting",
        kind: "demo_context",
        contextKind: missing.kind,
        label: "View demo preparation",
        title: "Explore the demo preparation state",
        description: "Demo mode stays on this leader and does not create a live meeting.",
        route: leaderRoute(scope.leaderId, { workspace: "brief" }),
        sourceLabel: "Demo meeting schedule",
        sourceDate: null,
        isWrite: false,
        requiresProcessing: false,
      };
    }
    const search = new URLSearchParams({ leaderId: scope.leaderId, action: "schedule" });
    return {
      id: "schedule-meeting",
      kind: "schedule_meeting",
      label: "Schedule meeting",
      title: "Set the next coaching conversation",
      description: "There is no confirmed upcoming meeting. Schedule one before preparing session-specific context.",
      route: `/coach/meetings?${search.toString()}`,
      sourceLabel: "Meeting schedule",
      sourceDate: null,
      isWrite: true,
      requiresProcessing: false,
    };
  }
  return null;
}

function resolveNotice(
  input: LeaderDetailDecisionInput,
  primaryAction: LeaderDetailPrimaryAction | null,
): LeaderDetailOperationalNotice | LeaderDetailDecisionPresentation | null {
  if (input.billing.status === "loading") return loading(input.scope, "billing");
  if (input.billing.status === "error") {
    return unavailable(input.scope, "billing_error", retryAction("retry_billing"));
  }
  if (input.billing.value.pendingResolution) {
    return {
      kind: "billing_resolution",
      tone: "warning",
      title: "Billing decision needed",
      description: "Review the authorized billing choices and decide how this relationship should continue.",
      action: { kind: "reveal_billing_resolution", label: "Review billing choices" },
    };
  }
  if (input.billing.value.processingLimitReached && primaryAction?.requiresProcessing) {
    return {
      kind: "processing_limit",
      tone: "critical",
      title: "Meeting processing is currently limited",
      description: "The transcript can be selected, but processing must wait for available capacity.",
      action: null,
    };
  }
  if (
    input.sourceReadiness.status === "ready"
    && input.sourceReadiness.value.affectsPrimaryAction
    && input.sourceReadiness.value.unavailableLabels.length > 0
  ) {
    return {
      kind: "source_readiness",
      tone: "warning",
      title: "Some action context is temporarily unavailable",
      description: `${input.sourceReadiness.value.unavailableLabels.join(", ")} could not be loaded.`,
      action: { kind: "retry_source_context", label: "Retry coaching context" },
    };
  }
  if (
    input.auxiliaryReadiness.status === "error"
    || (input.auxiliaryReadiness.status === "ready" && input.auxiliaryReadiness.value.unavailableLabels.length > 0)
  ) {
    const labels = input.auxiliaryReadiness.status === "ready"
      ? input.auxiliaryReadiness.value.unavailableLabels
      : [];
    return {
      kind: "auxiliary_unavailable",
      tone: "warning",
      title: "Some workspace details are temporarily unavailable.",
      description: labels.length > 0
        ? `${labels.join(", ")} could not be confirmed.`
        : "Retry the unavailable workspace details.",
      action: { kind: "retry_auxiliary_details", label: "Retry unavailable details" },
    };
  }
  return null;
}

export function buildLeaderDetailDecisionPresentation(
  input: LeaderDetailDecisionInput,
): LeaderDetailDecisionPresentation {
  const { scope } = input;
  if (input.access.status === "checking") return loading(scope, "access");
  if (input.access.status === "error") return unavailable(scope, "access_error", retryAction("retry_access"));
  if (input.access.status === "unavailable") {
    return unavailable(scope, "access_unavailable", {
      kind: "back_to_leaders",
      label: "Back to Leaders",
      route: "/coach/leaders",
    });
  }
  if (
    !isStableId(scope.coachId)
    || !isStableId(scope.leaderId)
    || !scope.relationshipRevision.trim()
    || !scope.accessGeneration.trim()
  ) return unavailable(scope, "invalid_source", retryAction("retry_access"));
  if (input.billing.status === "loading") return loading(scope, "billing");
  if (input.billing.status === "error") return unavailable(scope, "billing_error", retryAction("retry_billing"));
  if (input.workspace.status === "loading") return loading(scope, "workspace");
  if (input.workspace.status === "error") return unavailable(scope, "workspace_error", retryAction("retry_workspace"));
  if (input.sourceReadiness.status === "loading") return loading(scope, "context");
  if (input.sourceReadiness.status === "error") {
    return unavailable(scope, "source_error", retryAction("retry_context"));
  }

  const primaryAction = resolvePrimaryAction(input);
  if (primaryAction && "status" in primaryAction) return primaryAction;
  const normalizedPrimaryAction = primaryAction || null;
  const notice = resolveNotice(input, normalizedPrimaryAction);
  if (notice && "status" in notice) return notice;
  return { status: "ready", scope, primaryAction: normalizedPrimaryAction, notice };
}
